import { Button, Checkbox, FormControlLabel, InputAdornment, TextField } from '@mui/material';
import { CustomerTier, DriverReport, LanguageCode, PermissionType, Timezone, UnitsWeight } from 'harmony-constants';
import { L, UNITS } from 'harmony-language';
import React, { FormEvent, useState } from 'react';
import { useAddCompanyMutation } from '../../../../api/mutations/add/use-add-company-mutation';
import { useEditCompanyMutation } from '../../../../api/mutations/edit/use-edit-company-mutation';
import { CreateOrganization, Organization } from '../../../../types';
import { isIntegerAndMoreThanZeroAndLessThan600 } from '../../../../utils/validation-utils';
import { SelectLanguage } from '../../../shared/inputs/select-language';
import { SelectSiteIssueKeys } from '../../../shared/inputs/select-site-issue-keys';
import { SelectTimezone } from '../../../shared/inputs/select-timezone';
import { SelectUnits } from '../../../shared/inputs/select-units';
import { SelectUnitsWeight } from '../../../shared/inputs/select-units-weight';
import { ValidatedTextField } from '../../../shared/inputs/validated-text-field';
import { useConvertedUnitSpeed } from '../../../user/selectors/use-converted-unit';
import { usePermissions } from '../../../user/selectors/use-permissions';
import { MobileSettings } from './mobile-settings';
import { ThroughputSettings } from './throughput-settings';
import { useCompanyId } from '../../../../app/agistics-context';

const isValidLatitude = (latitude: number) => {
    return latitude >= -90 && latitude <= 90;
};

const isValidLongitude = (longitude: number) => {
    return longitude >= -180 && longitude <= 180;
};

interface EditCreateOrganizationProps {
    existingOrganization: Organization | null;
    onSubmit?: () => void;
}

export const EditCreateOrganization: React.FC<EditCreateOrganizationProps> = (props) => {
    const { existingOrganization, onSubmit } = props;
    const [organization, setOrganization] = useState<CreateOrganization | Organization>(existingOrganization || {
        name: '',
        language: LanguageCode.EnglishUS,
        timezone: Timezone.America_Chicago,
        units: UNITS.Imperial,
        siteIssueKeys: null,
        tier: CustomerTier.Standard,
        etaThresholdMinutes: 30,
        latitude: 0,
        longitude: 0,
        isCarrier: false,
        unitsWeight: UnitsWeight.Pound,
        cargoTypes: [],
        travelSpeed: null,
        settings: {
            driverReportStopActuals: DriverReport.no, // removed
            driverReportWeighStopWeight: DriverReport.no, // removed
            driverAssignCompartments: DriverReport.no,
            driverAllowStopCompletionOutOfSequence: false, // removed
            driverAllowStopStartOutOfSequence: false, // removed
            etaDelayThresholdMinutes: 60, // removed
            insideSiteInactiveMinutes: 30,
            outsideSiteInactiveMinutes: 10,
            enforceSameCustomerSplitLocations: false,
            navigateOnStart: false,
            upcoming: false,
        },
        // throughputs: [],
    });
    const canViewCompanies = usePermissions(PermissionType.ViewAllCompanies);
    const companyId = canViewCompanies ? null : useCompanyId();
    const addOrganizationMutation = useAddCompanyMutation();
    const editOrganizationMutation = useEditCompanyMutation(companyId);
    const { unitTranslation, metersPerSecondToLocalized, localizedToMetersPerSecond } = useConvertedUnitSpeed();
    const [ displaySpeed, setDisplaySpeed ] = useState<number | undefined>(
        organization.travelSpeed ? metersPerSecondToLocalized(organization.travelSpeed) : undefined
    );

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if ('id' in organization) {
            editOrganizationMutation.mutate({ company: organization });
        } else {
            addOrganizationMutation.mutate({ company: organization });
        }

        onSubmit && onSubmit();
    };

    const etaThresholdMinutesError = !isIntegerAndMoreThanZeroAndLessThan600(organization.etaThresholdMinutes);
    const insideSiteInactiveMinutesError = !isIntegerAndMoreThanZeroAndLessThan600(organization.settings.insideSiteInactiveMinutes);
    const outsideSiteInactiveMinutesError = !isIntegerAndMoreThanZeroAndLessThan600(organization.settings.outsideSiteInactiveMinutes);
    const anyError = etaThresholdMinutesError || insideSiteInactiveMinutesError || outsideSiteInactiveMinutesError;

    return (
        <form
            onSubmit={handleSubmit}
            style={{ width: '70vw', margin: '0 auto' }}
        >
            <div style={{ display: 'flex', flexDirection: 'row', gap: '2rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '.5rem' }}>
                    {canViewCompanies && <FormControlLabel
                        label={L.carrier()}
                        control={<Checkbox
                            disabled={existingOrganization ? existingOrganization.isCarrier : false}
                            checked={organization.isCarrier}
                            onChange={(e) => {
                                setOrganization({ ...organization, isCarrier: e.target.checked })
                            }}
                        />}
                    />}
                    <TextField
                        variant='standard'
                        label={L.name()}
                        fullWidth={true}
                        required={true}
                        value={organization.name}
                        onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
                    />
                    <SelectLanguage
                        language={organization.language}
                        onChange={(l) => setOrganization({ ...organization, language: l })}
                    />
                    <SelectTimezone
                        timezone={organization.timezone}
                        onChange={(t) => setOrganization({ ...organization, timezone: t })}
                    />
                    <SelectUnits
                        units={organization.units}
                        onChange={(u) => setOrganization({ ... organization, units: u })}
                    />
                    <SelectUnitsWeight
                        unitsWeight={organization.unitsWeight}
                        onChange={(u) => setOrganization({ ...organization, unitsWeight: u })}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                        <ValidatedTextField
                            type='number'
                            label={L.latitude()}
                            fullWidth={true}
                            required={true}
                            value={organization.latitude === 0 ? '' : organization.latitude}
                            onChange={(e) => {
                                const newLatitude = Number(e.target.value);
                                setOrganization({ ...organization, latitude: newLatitude });
                            }}
                            validations={[
                                {
                                    isValid: () => isValidLatitude(organization.latitude),
                                    message: L.invalidLatitude(),
                                }
                            ]}
                        />
                        <ValidatedTextField
                            type='number'
                            label={L.longitude()}
                            fullWidth={true}
                            required={true}
                            value={organization.longitude === 0 ? '' : organization.longitude}
                            onChange={(e) => {
                                const newLongitude = Number(e.target.value);
                                setOrganization({ ...organization, longitude: newLongitude });
                            }}
                            validations={[
                                {
                                    isValid: () => isValidLongitude(organization.longitude),
                                    message: L.invalidLongitude(),
                                }
                            ]}
                        />
                        <ValidatedTextField
                            type='number'
                            label={L.travelSpeed()}
                            fullWidth
                            value={displaySpeed || ''}
                            inputProps={{ min: 1, step: 1 }}
                            InputProps={{
                                endAdornment: <InputAdornment position='end'>{unitTranslation}</InputAdornment>,
                            }}
                            onChange={(e) => {
                                const speed = Number(e.target.value);
                                setDisplaySpeed(speed);
                                setOrganization(prev => ({
                                    ...prev,
                                    travelSpeed: speed > 0 ? localizedToMetersPerSecond(speed, 2) : null,
                                }))
                            }}
                        />
                    </div>
                    {!organization.isCarrier && existingOrganization && <SelectSiteIssueKeys
                        siteIssueKeys={organization.siteIssueKeys}
                        onChange={(e) => {
                            setOrganization({ ...organization, siteIssueKeys: e });
                        }}
                        cargoTypes={organization.cargoTypes}
                    />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {!organization.isCarrier && <div>
                        <ThroughputSettings
                            organization={organization}
                            onChange={(t) => {
                                setOrganization({ ...organization, throughputs: t });
                            }}
                        />
                        <MobileSettings
                            organization={organization}
                            setOrganization={setOrganization}
                            etaThresholdMinutesError={etaThresholdMinutesError}
                            insideSiteInactiveMinutesError={insideSiteInactiveMinutesError}
                            outsideSiteInactiveMinutesError={outsideSiteInactiveMinutesError}
                        />
                    </div>}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <Button
                    type='submit'
                    disabled={anyError}
                    variant='contained'
                >
                    {existingOrganization ? L.updateCompany() : L.createCompany()}
                </Button>
            </div>
        </form>
    );
};
